import * as Popover from "@radix-ui/react-popover";
import * as RadixSelect from "@radix-ui/react-select";
import { addMonths, format, formatISO, type Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import {
    type KeyboardEvent,
    forwardRef,
    useEffect,
    useId,
    useState,
} from "react";
import { DayPicker } from "react-day-picker";
import type { CaptionLayout } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Button } from "../Button";
import { cx } from "../../utils/cx";
import Icon from "../../../base/components/Icon";
import styles from "./Datepicker.module.scss";

const CalendarIcon = () => <Icon icon="calendar" />;

export interface DatepickerProps {
    /** Floating label (Figma: “Pick a date”). */
    label?: string;
    /**
     * Controlled value. Use with `onChange`. Pass `null` for no date.
     * Omit both `value` and `onChange` for uncontrolled mode (`defaultValue`).
     */
    value?: Date | null;
    /** Uncontrolled initial day. */
    defaultValue?: Date;
    /** Fires when a day is chosen or cleared from the calendar. */
    onChange?: (date: Date | undefined) => void;
    /** Helper below the field (12px tertiary in Figma). */
    helperText?: string;
    /** Error: boolean for style only, or string for message. */
    error?: boolean | string;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    className?: string;
    /** `date-fns` pattern for the trigger (default `MM/dd/yyyy`). */
    dateFormat?: string;
    fromDate?: Date;
    toDate?: Date;
    /** Submits `YYYY-MM-DD` when used inside a `<form>`. */
    name?: string;
    id?: string;
    locale?: Locale;
    /**
     * `buttons` — month chevrons only (Figma *Change year=No*).
     * `dropdown` — month/year selects (Figma *Change year=Yes*); set
     * `fromYear` / `toYear` or defaults 1900–2100 apply.
     */
    captionLayout?: CaptionLayout;
    /** Inclusive year bounds when `captionLayout="dropdown"`. */
    fromYear?: number;
    toYear?: number;
    /**
     * Figma *Buttons=Yes*: OK / Cancel row; selection applies on OK only.
     */
    showFooterActions?: boolean;
    /** Footer button labels. */
    footerLabels?: { ok?: string; cancel?: string };
}

/**
 * Treez DS — Datepicker (single date, Wave 1).
 *
 * Figma: trigger `656:45459`; flyout matrix `656:44976` → `656:44981`
 * (symbols: *Buttons* / *Change year* / *Type* — Date, Year, Date Range).
 * Wave 1 implements **single date** + optional dropdown caption + footer.
 * **Date range** and **year grid** types are not implemented yet.
 */
export const Datepicker = forwardRef<HTMLButtonElement, DatepickerProps>(
    function Datepicker(
        {
            label = "Pick a date",
            value,
            defaultValue,
            onChange,
            helperText,
            error,
            disabled = false,
            required,
            fullWidth = false,
            className,
            dateFormat = "MM/dd/yyyy",
            fromDate,
            toDate,
            name,
            id,
            locale = enUS,
            captionLayout = "buttons",
            fromYear,
            toYear,
            showFooterActions = false,
            footerLabels,
        },
        ref,
    ) {
        const reactId = useId();
        const fieldId = id ?? `ds-datepicker-${reactId}`;
        const messageId = `${fieldId}-msg`;

        const isControlled = onChange !== undefined;
        const [inner, setInner] = useState<Date | undefined>(defaultValue);
        const selected = isControlled ? value ?? undefined : inner;

        const hasError = Boolean(error);
        const message = typeof error === "string" ? error : helperText;
        const hasValue = Boolean(selected);

        const [open, setOpen] = useState(false);
        const [menuMonth, setMenuMonth] = useState<Date>(
            selected ?? new Date(),
        );
        const [pending, setPending] = useState<Date | undefined>(selected);

        useEffect(() => {
            if (open) {
                setMenuMonth(selected ?? new Date());
                setPending(selected);
            }
        }, [open, selected]);

        const displayText = selected
            ? format(selected, dateFormat, { locale })
            : "\u00a0";

        const commitSelection = (d: Date | undefined) => {
            if (!isControlled) {
                setInner(d);
            }
            onChange?.(d);
        };

        const handleSelect = (d: Date | undefined) => {
            if (showFooterActions) {
                setPending(d);
                return;
            }
            commitSelection(d);
            setOpen(false);
        };

        const footerOk = footerLabels?.ok ?? "OK";
        const footerCancel = footerLabels?.cancel ?? "Cancel";

        const yearFrom = fromYear ?? 1900;
        const yearTo = toYear ?? 2100;
        const usesCustomDropdownCaption =
            captionLayout === "dropdown" ||
            captionLayout === "dropdown-buttons";
        const monthValue = String(menuMonth.getMonth());
        const yearValue = String(menuMonth.getFullYear());
        const monthOptions = Array.from({ length: 12 }, (_, idx) => ({
            value: String(idx),
            label: format(new Date(2020, idx, 1), "MMMM", { locale }),
        }));
        const yearOptions = Array.from(
            { length: Math.max(0, yearTo - yearFrom + 1) },
            (_, idx) => String(yearFrom + idx),
        );

        const goPrevMonth = () =>
            setMenuMonth(current => addMonths(current, -1));
        const goNextMonth = () =>
            setMenuMonth(current => addMonths(current, 1));

        const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
            if (disabled) return;
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setOpen(o => !o);
            }
        };

        const hiddenValue = selected
            ? formatISO(selected, { representation: "date" })
            : "";

        return (
            <div
                className={cx(
                    styles.root,
                    fullWidth && styles.fullWidth,
                    className,
                )}
                data-disabled={disabled || undefined}
                data-error={hasError || undefined}
                data-has-value={hasValue || undefined}
                data-open={open || undefined}
                data-ui="datepicker"
            >
                {name ? (
                    <input
                        aria-hidden
                        name={name}
                        readOnly
                        tabIndex={-1}
                        type="hidden"
                        value={hiddenValue}
                    />
                ) : null}

                <Popover.Root onOpenChange={setOpen} open={open}>
                    <Popover.Trigger asChild>
                        <button
                            aria-describedby={message ? messageId : undefined}
                            aria-expanded={open}
                            aria-haspopup="dialog"
                            aria-invalid={hasError || undefined}
                            aria-labelledby={`${fieldId}-lbl ${fieldId}-val`}
                            className={styles.field}
                            disabled={disabled}
                            id={fieldId}
                            onKeyDown={onTriggerKeyDown}
                            ref={ref}
                            type="button"
                        >
                            <span className={styles.inner}>
                                <span
                                    className={styles.display}
                                    id={`${fieldId}-val`}
                                >
                                    {displayText}
                                </span>
                                <span
                                    className={styles.floatingLabel}
                                    id={`${fieldId}-lbl`}
                                >
                                    {label}
                                    {required ? " *" : ""}
                                </span>
                            </span>
                            <span className={styles.iconRight}>
                                <CalendarIcon />
                            </span>
                        </button>
                    </Popover.Trigger>

                    <Popover.Portal>
                        <Popover.Content
                            align="start"
                            className={cx(
                                styles.flyout,
                                usesCustomDropdownCaption &&
                                    styles.flyoutCustomCaption,
                            )}
                            collisionPadding={16}
                            side="bottom"
                            sideOffset={4}
                        >
                            {usesCustomDropdownCaption ? (
                                <div className={styles.customCaption}>
                                    <RadixSelect.Root
                                        onValueChange={nextMonth =>
                                            setMenuMonth(
                                                new Date(
                                                    Number(yearValue),
                                                    Number(nextMonth),
                                                    1,
                                                ),
                                            )
                                        }
                                        value={monthValue}
                                    >
                                        <RadixSelect.Trigger
                                            className={styles.captionSelect}
                                            type="button"
                                        >
                                            <RadixSelect.Value />
                                        </RadixSelect.Trigger>
                                        <RadixSelect.Portal>
                                            <RadixSelect.Content
                                                className={
                                                    styles.captionSelectMenu
                                                }
                                                position="popper"
                                                sideOffset={4}
                                            >
                                                <RadixSelect.Viewport>
                                                    {monthOptions.map(opt => (
                                                        <RadixSelect.Item
                                                            className={
                                                                styles.captionSelectItem
                                                            }
                                                            key={opt.value}
                                                            value={opt.value}
                                                        >
                                                            <RadixSelect.ItemText>
                                                                {opt.label}
                                                            </RadixSelect.ItemText>
                                                        </RadixSelect.Item>
                                                    ))}
                                                </RadixSelect.Viewport>
                                            </RadixSelect.Content>
                                        </RadixSelect.Portal>
                                    </RadixSelect.Root>

                                    <RadixSelect.Root
                                        onValueChange={nextYear =>
                                            setMenuMonth(
                                                new Date(
                                                    Number(nextYear),
                                                    Number(monthValue),
                                                    1,
                                                ),
                                            )
                                        }
                                        value={yearValue}
                                    >
                                        <RadixSelect.Trigger
                                            className={styles.captionSelect}
                                            type="button"
                                        >
                                            <RadixSelect.Value />
                                        </RadixSelect.Trigger>
                                        <RadixSelect.Portal>
                                            <RadixSelect.Content
                                                className={
                                                    styles.captionSelectMenu
                                                }
                                                position="popper"
                                                sideOffset={4}
                                            >
                                                <RadixSelect.Viewport>
                                                    {yearOptions.map(opt => (
                                                        <RadixSelect.Item
                                                            className={
                                                                styles.captionSelectItem
                                                            }
                                                            key={opt}
                                                            value={opt}
                                                        >
                                                            <RadixSelect.ItemText>
                                                                {opt}
                                                            </RadixSelect.ItemText>
                                                        </RadixSelect.Item>
                                                    ))}
                                                </RadixSelect.Viewport>
                                            </RadixSelect.Content>
                                        </RadixSelect.Portal>
                                    </RadixSelect.Root>

                                    <div className={styles.captionNav}>
                                        <button
                                            className={styles.captionNavBtn}
                                            onClick={goPrevMonth}
                                            type="button"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            className={styles.captionNavBtn}
                                            onClick={goNextMonth}
                                            type="button"
                                        >
                                            ›
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                            <DayPicker
                                captionLayout={
                                    usesCustomDropdownCaption
                                        ? "buttons"
                                        : captionLayout
                                }
                                disabled={disabled}
                                fromDate={fromDate}
                                fromYear={
                                    captionLayout === "dropdown"
                                        ? yearFrom
                                        : undefined
                                }
                                locale={locale}
                                mode="single"
                                month={menuMonth}
                                onMonthChange={setMenuMonth}
                                onSelect={handleSelect}
                                selected={
                                    showFooterActions ? pending : selected
                                }
                                toDate={toDate}
                                toYear={
                                    captionLayout === "dropdown"
                                        ? yearTo
                                        : undefined
                                }
                                weekStartsOn={0}
                            />
                            {showFooterActions ? (
                                <div className={styles.footer}>
                                    <Button
                                        onClick={() => setOpen(false)}
                                        size="sm"
                                        type="button"
                                        variant="secondary"
                                    >
                                        {footerCancel}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            commitSelection(pending);
                                            setOpen(false);
                                        }}
                                        size="sm"
                                        type="button"
                                        variant="primary"
                                    >
                                        {footerOk}
                                    </Button>
                                </div>
                            ) : null}
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>

                {message ? (
                    <div className={styles.helper} id={messageId}>
                        {message}
                    </div>
                ) : null}
            </div>
        );
    },
);

Datepicker.displayName = "Datepicker";
